// Client-side mirror of dprEngine.js's computeCapacityConsumption, trimmed to
// what the wizard's live "Raw Material Procurement" preview needs. Kept here
// rather than imported from ca-backend since CRA's webpack build can't reach
// outside src/ — same pattern as dprLoanCalc.js.
const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100;

// Year 1 is the actual/base figure — escalation compounds starting Year 2.
function escalatedValue(base, flatPct, yearIndex) {
  let value = base;
  for (let y = 2; y <= yearIndex; y++) value = value * (1 + (flatPct || 0) / 100);
  return value;
}

// Returns one row per projection year: { year, utilPct, monthsThisYear,
// productionQty, lines: [{ name, unit, qty, unitCost, openingStockQty,
// closingStockQty, purchasedQty, procurementCost }] } — same shape/formulas
// as dprEngine.js's computeCapacityConsumption, so the wizard's live preview
// matches the saved report exactly once the case is recomputed server-side.
export function computeRmProcurementPreview(caseData) {
  const cap = caseData.capacity_config || {};
  const rm = caseData.raw_material || {};
  const projectionYears = caseData.projection_years || 6;

  const rampUp = (cap.rampUpPctByYear && cap.rampUpPctByYear.length)
    ? cap.rampUpPctByYear
    : Array.from({ length: projectionYears }, (_, i) => Math.min(30 + i * 5, 100));

  const installedPerHour = cap.installedCapacityPerHour || 0;
  const hoursPerDay = (cap.hoursPerDay || 8) * (cap.shiftsPerDay || 1);
  const workingDays = cap.workingDaysPerYear || 300;
  const wastagePct = cap.wastagePct || 0;
  const installedAnnualQty = installedPerHour * hoursPerDay * workingDays * (1 - wastagePct / 100);

  const recipeLines = rm.recipeLines || [];
  const rmStockDaysDefault = rm.rmStockDays ?? 30;
  const normalOperatingMonths = cap.normalOperatingMonths || 12;
  const operatingMonths = cap.operatingMonthsByYear || [];
  const prevClosingByLine = recipeLines.map(() => 0);

  return Array.from({ length: projectionYears }, (_, idx) => {
    const y = idx + 1;
    const utilPct = rampUp[idx] !== undefined ? rampUp[idx] : rampUp[rampUp.length - 1];
    const monthsThisYear = operatingMonths[idx] !== undefined ? operatingMonths[idx] : normalOperatingMonths;
    const productionQty = installedAnnualQty * (utilPct / 100) * (monthsThisYear / normalOperatingMonths);

    const lines = recipeLines.map((line, li) => {
      const escalatedUnitCost = escalatedValue(line.unitCost || 0, line.escalationPct, y);
      const qty = (line.qtyPerOutputUnit || 0) * productionQty;
      const stockDays = line.closingStockDays ?? rmStockDaysDefault;
      const openingStockQty = prevClosingByLine[li];
      const closingStockQty = round2(qty * stockDays / workingDays);
      const purchasedQty = round2(qty + closingStockQty - openingStockQty);
      const procurementCost = round2(purchasedQty * escalatedUnitCost);
      prevClosingByLine[li] = closingStockQty;
      return {
        name: line.name, unit: line.unit || '', qty: round2(qty), unitCost: round2(escalatedUnitCost),
        openingStockQty: round2(openingStockQty), closingStockQty, purchasedQty, procurementCost,
      };
    });

    return { year: y, utilPct, monthsThisYear, productionQty: round2(productionQty), lines };
  });
}
